/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/naming-convention */
import {
  ChevronLeft,
  FileText,
  ChevronDown,
  EllipsisVertical,
  PenLine,
  Trash2,
  CircleFadingArrowUp,
  LoaderCircle,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router';
import parse from 'html-react-parser';
import { format } from 'date-fns';
import { useEffect } from 'react';
import ReactPDF from '@react-pdf/renderer';
import LayoutAdmin from '../../../components/layouts/LayoutAdmin';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../../components/ui/breadcrumb';

import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import useIsMobile from '../../../hooks/use-mobile';
import useAuth from '../../../stores/useAuth';
import Skeleton from '../../../components/ui/skeleton';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/ui/dialog';
import useReceipt from '../../../stores/useReceipt';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import PaymentStatusBadge from '../../../components/ui/badge';
import { Table, TableCell, TableRow } from '../../../components/ui/table';
import { numberToWords } from '../../../utils/format';
import ReceiptCanvas from './ReceiptCanvas';
import { ICompany, IReceipt, IReceiptDetail } from '../../../types/types';

export default function ViewReceiptPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { receipt, loading, getReceipt, resetReceipt, deleteReceipt, fetchReceiptDetail, receiptDetails } =
    useReceipt();
  const { receiptId } = useParams();
  const { company } = useAuth();

  useEffect(() => {
    if (receiptId) {
      getReceipt(receiptId);
      fetchReceiptDetail(receiptId);
    }

    return () => {
      resetReceipt();
    };
  }, [receiptId, getReceipt, resetReceipt, fetchReceiptDetail]);

  const parsedDesc = parse(receipt?.description || '');
  const amount = new Intl.NumberFormat('id-ID').format(receipt?.amount ?? 0);

  return (
    <LayoutAdmin header="Receipt">
      <div className="flex gap-4 items-center">
        <button
          type="button"
          className="flex items-center gap-1 text-xs md:text-base cursor-pointer font-medium text-secondary"
          onClick={() => navigate('/receipt')}
        >
          <ChevronLeft size={16} />
          Back
        </button>
        <div className="w-2 h-2 rounded-full bg-slate-100" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink>
                <Link to="/receipt" className="text-xs md:text-base">
                  Sales Receipt
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-xs md:text-base">View Sales Receipt</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className="flex justify-between items-center">
        <div className="flex flex-wrap gap-2 items-center">
          <FileText className="w-4 h-4 md:w-6 md:h-6" />
          {loading ? (
            <Skeleton className="w-40 h-6 rounded-md" />
          ) : (
            <p className="md:text-lg text-sm lg:text-xl font-medium">{receipt?.receipt_number}</p>
          )}
        </div>
        <Dialog>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {!loading && (
                <Button type="button" variant={isMobile ? 'outline' : 'default'}>
                  {!isMobile ? (
                    <>
                      Action
                      <ChevronDown />
                    </>
                  ) : (
                    <EllipsisVertical />
                  )}
                </Button>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => navigate(`/receipt/edit/${receipt?.receipt_id}`)}>
                  <PenLine />
                  <span>Edit</span>
                </DropdownMenuItem>
                <DialogTrigger className="w-full">
                  <DropdownMenuItem>
                    <Trash2 />
                    <span>Delete</span>
                  </DropdownMenuItem>
                </DialogTrigger>
                <ReactPDF.PDFDownloadLink
                  document={
                    <ReceiptCanvas
                      company={company as ICompany}
                      receipt={receipt as IReceipt}
                      receiptDetails={receiptDetails as IReceiptDetail[]}
                    />
                  }
                  fileName={receipt?.receipt_number}
                >
                  <DropdownMenuItem>
                    <CircleFadingArrowUp />
                    <span>Download PDF</span>
                  </DropdownMenuItem>
                </ReactPDF.PDFDownloadLink>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <DialogContent className="w-[calc(100dvw-32px)] md:w-full rounded-lg">
            <DialogHeader>
              <DialogTitle>Confirm delete receipt</DialogTitle>
              <DialogDescription className="py-4">
                are you sure you want to delete receipt <span className="font-semibold">{receipt?.receipt_number}</span>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-row justify-end gap-2">
              <div>
                <Button variant="default" type="button" onClick={() => deleteReceipt(receipt?.receipt_id as string)}>
                  Delete
                </Button>
              </div>
              <DialogClose>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <Card className="p-4 flex-1">
          {loading ? (
            <div className="flex justify-center items-center h-full gap-2">
              <LoaderCircle className="animate-spin" />
              <p>Loading receipt data...</p>
            </div>
          ) : (
            <>
              {/* header receipt */}
              <div className="flex justify-between gap-2">
                <img src={company?.logo} alt="Logo" className="rounded-lg w-16 h-16 lg:w-28 lg:h-28" />
                <div className="flex">
                  <div className="flex flex-col gap-2 flex-1">
                    <p className="text-blue-700 font-medium text-right text-[9px] lg:text-sm">Receipt</p>
                    <div className="grid grid-cols-2 text-xs gap-2">
                      <div className="text-left flex flex-wrap gap-2 justify-end">
                        <p className="font-medium text-[9px] lg:text-sm">Payment No.</p>
                        <p className="text-[9px] lg:text-sm">{receipt?.receipt_number} </p>
                      </div>
                      <div className="flex text-xs gap-2 justify-end">
                        <p className="font-semibold text-right text-[9px] lg:text-sm">Invoice No.</p>
                        <div className="flex flex-col gap-1 text-[9px] lg:text-sm">
                          {receiptDetails?.map((detail) => (
                            <p key={detail.invoice_id} className="w-max">
                              {detail.invoice?.invoice_number}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* company information */}
              <div className="flex flex-col text-xs gap-1 mt-4">
                <p className="text-sm text-text font-semibold text-[9px] lg:text-sm">{company?.name}</p>
                <p className="text-[9px] lg:text-sm">Address: {company?.address_line_1}</p>
                <p className="text-[9px] lg:text-sm">
                  {company?.address_line_2} {company?.city ? `${company?.city}, ` : ''} {company?.state}
                </p>
                <p className="text-[9px] lg:text-sm">
                  {company?.zip_code ? `${company?.zip_code}, ` : ''} {company?.country}
                </p>
                {company?.phone ? <p className="text-[9px] lg:text-sm">{`Phone: ${company?.phone}`}</p> : ''}
                {company?.email ? <p className="text-[9px] lg:text-sm">{`Email: ${company?.email}`}</p> : ''}
              </div>
              <hr className="my-4" />
              <Table>
                <TableRow className="border-none">
                  <TableCell className="w-[100px] lg:w-36 text-[9px] lg:text-sm">Received from</TableCell>
                  <TableCell className="w-5 text-[9px] lg:text-sm">:</TableCell>
                  <TableCell className="font-semibold text-[9px] lg:text-sm">{receipt?.partner?.name}</TableCell>
                </TableRow>
                <TableRow className="border-none">
                  <TableCell className="w-[100px] lg:w-36 text-[9px] lg:text-sm">Amount</TableCell>
                  <TableCell className="w-5 text-[9px] lg:text-sm">:</TableCell>
                  <TableCell className="font-semibold bg-blue-50 text-blue-800 text-[9px] lg:text-sm">
                    {amount}
                  </TableCell>
                </TableRow>
              </Table>
              <hr className="my-4" />
              <Table>
                <TableRow className="border-none">
                  <TableCell className="w-[100px] lg:w-36 text-[9px] lg:text-sm">Amount</TableCell>
                  <TableCell className="w-5 text-[9px] lg:text-sm">:</TableCell>
                  <TableCell className="font-semibold bg-blue-50 text-blue-800 text-[9px] lg:text-sm">
                    {numberToWords(receipt?.amount ?? 0)}
                  </TableCell>
                </TableRow>
                <TableRow className="border-none">
                  <TableCell className="w-[100px] lg:w-36 text-[9px] lg:text-sm">For Payment of</TableCell>
                  <TableCell className="w-5 text-[9px] lg:text-sm">:</TableCell>
                  <TableCell className="font-semibold text-[9px] lg:text-sm">{parsedDesc}</TableCell>
                </TableRow>
              </Table>
              <hr className="my-4" />
              <div className="flex justify-end my-8">
                <div className="flex flex-col gap-2 text-center w-1/3 items-center">
                  <p className="text-[9px] lg:text-sm font-medium">
                    {receipt?.date ? format(receipt.date, 'LLL dd, y') : 'N/A'}
                  </p>
                  {company?.signature ? (
                    <>
                      <img src={company?.signature} alt="Signature" className="rounded-lg w-28 h-28" />
                      <p className="font-semibold text-[9px] lg:text-sm">{company?.name}</p>
                    </>
                  ) : (
                    <div className="w-28 h-28" />
                  )}
                </div>
              </div>
            </>
          )}
        </Card>
        <div className="flex flex-col gap-2 md:w-1/3 w-full">
          <Card className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-2 h-max w-full">
            {loading ? (
              <>
                <Skeleton className="w-full h-6 rounded-md" />
                <Skeleton className="w-full h-6 rounded-md" />
                <Skeleton className="w-full h-6 rounded-md mt-2" />
                <Skeleton className="w-full h-6 rounded-md mt-2" />
                <Skeleton className="w-full h-6 rounded-md mt-2" />
                <Skeleton className="w-full h-6 rounded-md mt-2" />
              </>
            ) : (
              <>
                <div>
                  <p className="text-sm md:text-base">Partner</p>
                  <p className="text-sm md:text-base font-semibold">{receipt?.partner?.name}</p>
                </div>
                <div>
                  <p className="text-sm md:text-base">Payment Method</p>
                  <p className="text-sm md:text-base font-semibold">{receipt?.method}</p>
                </div>
                <div>
                  <p className="text-sm md:text-base">Date</p>
                  <p className="text-sm md:text-base font-semibold">
                    {receipt?.date ? format(receipt.date, 'LLL dd, y') : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm md:text-base">Total Amount</p>
                  <p className="text-sm md:text-base font-semibold">
                    {Intl.NumberFormat('id-ID', {
                      style: 'currency',
                      currency: 'IDR',
                    }).format(receipt?.amount ?? 0)}
                  </p>
                </div>
              </>
            )}
          </Card>
          <Card className="flex flex-col gap-2">
            {loading ? (
              <div className="p-2">
                <Skeleton className="w-full h-10 rounded-md mt-2" />
                <Skeleton className="w-full h-10 rounded-md mt-2" />
                <Skeleton className="w-full h-10 rounded-md mt-2" />
                <Skeleton className="w-full h-10 rounded-md mt-2" />
              </div>
            ) : (
              <>
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-sm md:text-base">Invoice</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 flex flex-col gap-2">
                  {receiptDetails?.map((detail) => (
                    <Link to={`/invoice/view/${detail.invoice_id}`} key={detail.invoice_id}>
                      <Card key={detail.invoice_id} className="hover:bg-gray-50 transition-all">
                        <CardContent className="p-2">
                          <div className="flex justify-between items-center">
                            <p className="text-sm md:text-base">{detail.invoice?.invoice_number}</p>
                            <PaymentStatusBadge status={detail.invoice?.status_payment as string} />
                          </div>
                          <p className="font-semibold text-sm md:text-base">
                            {Intl.NumberFormat('id-ID', {
                              style: 'currency',
                              currency: 'IDR',
                            }).format(detail.invoice?.amount_due ?? 0)}
                          </p>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
    </LayoutAdmin>
  );
}
